import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Stack,
  Grid,
  CircularProgress,
  Card,
  TablePagination,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
} from "@mui/icons-material";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../firebase";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

function Ledger() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [ledgers, setLedgers] = useState([]);
  const [filteredLedgers, setFilteredLedgers] = useState([]);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    ledgername: "",
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchLedgers(user.uid);
      } else {
        setLedgers([]);
        setFilteredLedgers([]);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const filtered = ledgers.filter((item) =>
      item.ledgername
        ?.toLowerCase()
        .includes(search.toLowerCase())
    );

    setFilteredLedgers(filtered);
    setPage(0);
  }, [search, ledgers]);

  const fetchLedgers = async (uid) => {
    const userId = uid || auth.currentUser?.uid;
    if (!userId) return;

    try {
      const q = query(
        collection(db, "ledgers")
      );

      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setLedgers(data);
      setFilteredLedgers(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load ledgers");
    }
  };

  const handleOpen = () => {
    setFormData({
      ledgername: "",
    });

    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.ledgername.trim()) {
      toast.error("Ledger Name is required");
      return;
    }

    try {
      setLoading(true);
      const propid = auth.currentUser?.uid;

      if (!propid) {
        toast.error("User not found");
        return;
      }

      if (editMode && editId) {
        await updateDoc(doc(db, "ledgers", editId), {
          ledgername: formData.ledgername,
          propid,
        });

        toast.success("Ledger Updated Successfully");
      } else {
        await addDoc(collection(db, "ledgers"), {
          ledgername: formData.ledgername,
          propid,
          createdAt: new Date(),
        });

        toast.success("Ledger Added Successfully");
      }

      await fetchLedgers(propid);
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    }

    setLoading(false);
  };

  const handleEdit = (item) => {
    setFormData({
      ledgername: item.ledgername || "",
    });

    setEditMode(true);
    setEditId(item.id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Ledger?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes Delete",
      confirmButtonColor: "#d32f2f",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteDoc(doc(db, "ledgers", id));

      toast.success("Ledger Deleted Successfully");
      fetchLedgers();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete ledger");
    }
  };

  const paginatedData = filteredLedgers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box p={1}>
      <Card sx={{ p: 1.5 }}>
        {/* Compact Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.5}
          mb={1.5}
        >
          <Typography variant="h6" fontWeight="bold">
            Ledger Master
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search Ledger..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: "100%", sm: 200 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                style: { height: 32, fontSize: "0.875rem" }
              }}
            />

            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleOpen}
              sx={{ height: 32, textTransform: "none" }}
            >
              Add Ledger
            </Button>
          </Stack>
        </Stack>

        {/* Small sized Table */}
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell width={60} sx={{ py: 1 }}>
                  <b>Sr No</b>
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <b>Ledger Name</b>
                </TableCell>
                <TableCell align="center" width={120} sx={{ py: 1 }}>
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    No Ledger Found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ py: 0.5 }}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {item.ledgername}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredLedgers.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{
              ".MuiTablePagination-toolbar": { minHeight: 40 },
              ".MuiTablePagination-selectRoot": { marginY: 0 }
            }}
          />
        </Paper>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5, fontSize: "1.1rem" }}>
          {editMode ? "Edit Ledger" : "Add Ledger"}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Ledger Name"
                name="ledgername"
                value={formData.ledgername}
                onChange={handleChange}
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button color="error" size="small" onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : editMode ? (
              "Update"
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Ledger;